/**
 * Referencia de la solicitud de re-vinculación en curso (T039; US3).
 *
 * Vive en `sessionStorage` (nunca en `localStorage`, implementation-notes 002 §4): así el
 * trabajador puede volver a `/dispositivo-no-registrado` y recuperar el seguimiento de su
 * solicitud sin escribir de nuevo el documento, y todo desaparece al cerrar la pestaña.
 *
 * El `ref` (`rbq_…`) es **opaco**: no revela si existe un trabajador con ese documento (spec US3,
 * escenario 6). No se muestra en pantalla ni se loguea; solo se usa para construir la URL de
 * `GET /rebind-requests/{ref}`.
 */
export const REBIND_REQUEST_STORAGE_KEY = 'fermin:rebind-request';

export interface StoredRebindRequest {
  /** `request_ref` devuelto por `POST /rebind-requests` (202). */
  ref: string;
  /** Marca ISO del envío, para mostrar "hace cuánto" en una iteración posterior. */
  requestedAt: string;
}

function isStoredRebindRequest(value: unknown): value is StoredRebindRequest {
  if (value === null || typeof value !== 'object') {
    return false;
  }
  const candidate = value as Record<string, unknown>;
  return typeof candidate.ref === 'string' && typeof candidate.requestedAt === 'string';
}

/** Solicitud guardada, o `null` si no hay ninguna (o no hay almacenamiento disponible). */
export function getRebindRequest(): StoredRebindRequest | null {
  try {
    const raw = globalThis.sessionStorage?.getItem(REBIND_REQUEST_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed: unknown = JSON.parse(raw);
    return isStoredRebindRequest(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function setRebindRequest(value: StoredRebindRequest): void {
  try {
    globalThis.sessionStorage?.setItem(REBIND_REQUEST_STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Modo privado estricto: el seguimiento sigue en memoria mientras no se recargue la pantalla.
  }
}

/** Se llama al reclamar la vinculación (200) y cuando la solicitud queda `REJECTED`/`EXPIRED`. */
export function clearRebindRequest(): void {
  try {
    globalThis.sessionStorage?.removeItem(REBIND_REQUEST_STORAGE_KEY);
  } catch {
    // Sin almacenamiento no hay nada que borrar.
  }
}
